import { useState } from "react";
import moment from "moment";
import { updateClientMessage, CreateClientMessage } from "../api/Clients";

/**
 * Hook to create or update a client/product info message.
 *
 * @param {Object}   options
 * @param {number|null} options.clientId
 * @param {number|null} options.productId
 * @param {Function} [options.onSuccess]  Called with a success message string on save.
 * @param {Function} [options.onRefresh] Called after a successful API response to reload data.
 */
const useClientMessage = ({
  clientId = null,
  productId = null,
  onSuccess,
  onRefresh,
} = {}) => {
  const [error, setError] = useState({
    message: "",
    severity: "error",
    open: false,
  });

  const [snackbarAlert, setSnackbarAlert] = useState({
    open: false,
    message: "",
    data: {},
  });

  /**
   * Create or Update a client message.
   * @param {Object}  message - Message data (id, message, startDate, endDate, published_at, …)
   * @param {boolean} [update=false] - When true, clears the message (sets published_at to null)
   */
  const createUpdateMessage = (message, update = false) => {
    // Work on a shallow copy to avoid mutating the original object
    const payload = { ...message, clientId, productId };

    // Determine action based on whether the record already exists
    const action =
      payload.id && payload.id !== ""
        ? updateClientMessage
        : CreateClientMessage;

    // Format dates for the API
    payload.startDate = payload.startDate
      ? moment(payload.startDate).format("YYYY-MM-DD")
      : "";
    payload.endDate = payload.endDate
      ? moment(payload.endDate).format("YYYY-MM-DD")
      : "";

    // Clear mode: deactivate the message
    if (update) {
      payload.published_at = null;
    }

    action(payload).then((result) => {
      if (
        payload.id !== "" &&
        result?.data?.updateInformationMessage?.informationMessage
      ) {
        onRefresh?.();
        if (update) {
          // Show a snackbar with an undo action
          setSnackbarAlert({
            open: true,
            message: "Message Cleared",
            data: { ...payload, published_at: moment().format() },
          });
        } else {
          onSuccess?.("Message Updated Successfully", {
            ...payload,
            id: result?.data?.updateInformationMessage?.informationMessage?.id,
          });
        }
      } else if (result?.data?.createInformationMessage?.informationMessage) {
        onRefresh?.();
        onSuccess?.("Message Created Successfully", {
          ...payload,
          id: result?.data?.createInformationMessage?.informationMessage?.id,
        });
      } else {
        setError({
          open: true,
          message: "An error occurred, please try again",
          severity: "error",
        });
      }
    });
  };

  const handleCloseSnackbar = () =>
    setError({ open: false, message: "", severity: "info" });

  const handleCloseSnackbarAlert = () =>
    setSnackbarAlert({ open: false, message: "", data: {} });

  return {
    createUpdateMessage,
    error,
    setError,
    snackbarAlert,
    handleCloseSnackbar,
    handleCloseSnackbarAlert,
  };
};

export default useClientMessage;
